import axios from "axios";
import { useState, useEffect, useRef } from "react";
import profile from "../../../Assets/Home/noImage.jpg"
import { useNavigate } from "react-router-dom";

function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, visible];
}



function StarRating({ rating }) {
  return (
    <div className="flex gap-1 my-3">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          viewBox="0 0 24 24"
          className="w-5 h-5"
          fill={star <= rating ? "#fff220" : "none"}
          stroke={star <= rating ? "#fff220" : "#4a5568"}
          strokeWidth="1.5"
        >
          <path d="M12 2l2.39 7.26H22l-6.19 4.51 2.39 7.26L12 15.52l-6.2 4.51 2.39-7.26L2 9.27h7.61L12 2z" />
        </svg>
      ))}
    </div>
  );
}



export default function ClientReviews() {
  const [active, setActive] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [leftRef, leftVisible] = useInView(0.1);
  const [rightRef, rightVisible] = useInView(0.1);
  const [testimonials, setTestimonials] = useState([]);
  const navigate = useNavigate();


  const fetchTestimonial = async ()=> {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/testimonial/get-all`);
      setTestimonials(res.data.data);
    } catch (error) {
      console.log("Error", error);
    }
  }

  useEffect(() => {
    fetchTestimonial();
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;700;800;900&family=DM+Sans:wght@400;500&display=swap";
    document.head.appendChild(link);
  }, []);

  // Auto-cycle testimonials
useEffect(() => {
  if (testimonials.length === 0) return;

  const timer = setInterval(() => {
    setActive((prev) => (prev + 1) % testimonials.length);
  }, 4500);

  return () => clearInterval(timer);
}, [testimonials.length]);

  function handleChange(idx) {
    if (idx === active || animating) return;
    setAnimating(true);
    setTimeout(() => {
      setActive(idx);
      setAnimating(false);
    }, 300);
  }

  const t = testimonials[active] || {};

  // useEffect(()=>console.log("first", t),[]);

  return (
    <div
      className=" flex items-center justify-center py-20 px-6 md:px-10 lg:px-12 xl:px-20 relative overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #f0f4f8 0%, #e8edf5 50%, #f0f4f8 100%)",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      {/* Dot grid */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(circle, #42f551 1px, transparent 1px)",
          backgroundSize: "40px 40px",
          opacity: 0.3,
        }}
      />

      <div className="relative w-full ">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 items-stretch rounded-3xl overflow-hidden shadow-2xl"
          style={{ boxShadow: "0 32px 80px rgba(0,0,0,0.12)" }}
        >

          {/* LEFT — Image */}
          <div
            ref={leftRef}
            className="relative min-h-[420px] lg:min-h-[560px]"
            style={{
              opacity: leftVisible ? 1 : 0,
              transform: leftVisible ? "translateX(0)" : "translateX(-40px)",
              transition: "opacity 0.8s ease, transform 0.8s cubic-bezier(0.34,1.2,0.64,1)",
            }}
          >
            <img
              src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=900&q=80"
              alt="Construction team"
              className="w-full h-full object-cover absolute inset-0"
            />
            {/* Overlay */}
            <div
              className="absolute inset-0"
              style={{
                background: "linear-gradient(to right, rgba(10,20,50,0.15) 0%, transparent 60%)",
              }}
            />

            {/* Floating stat pill */}
            <div
              className="absolute bottom-8 left-8 right-8 rounded-2xl px-6 py-4 flex items-center justify-between"
              style={{
                background: "rgba(10,20,50,0.82)",
                backdropFilter: "blur(14px)",
                border: "1px solid rgba(255,255,255,0.1)",
              }}
            >
              {[
                { value: "500+", label: "Projects Done" },
                { value: "98%", label: "Satisfaction" },
                { value: "23+", label: "Years Active" },
              ].map((stat, i) => (
                <div key={i} className={`text-center ${i !== 2 ? "border-r border-white/10 pr-5 mr-5" : ""}`}>
                  <p
                    className="text-white font-black text-xl leading-none"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                  >
                    {stat.value}
                  </p>
                  <p className="text-blue-200/60 text-[10px] uppercase tracking-widest mt-1">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — Content */}
          <div
            ref={rightRef}
            className="flex flex-col justify-center px-10 py-12"
            style={{
              background: "linear-gradient(135deg, #f7f9fc, #eef2f8)",
              opacity: rightVisible ? 1 : 0,
              transform: rightVisible ? "translateX(0)" : "translateX(40px)",
              transition: "opacity 0.8s ease 0.2s, transform 0.8s cubic-bezier(0.34,1.2,0.64,1) 0.2s",
            }}
          >
            {/* Label */}
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-6 bg-green-400" />
              <span
                className="text-green-500 font-bold text-xs tracking-[0.25em] uppercase"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                Client Reviews
              </span>
            </div>

            {/* Heading */}
            <h2
              className="font-black uppercase text-gray-900 leading-[1.0] mb-7"
              style={{
                fontFamily: "'Barlow Condensed', sans-serif",
                fontSize: "clamp(26px, 3.2vw, 44px)",
                letterSpacing: "-0.01em",
              }}
            >
              Trusted By Clients{" "}
              <span
                style={{
                  WebkitTextStroke: "2px #0f1f4b",
                  color: "transparent",
                }}
              >
                Across
              </span>{" "}
              Every Industry
            </h2>

            {/* Testimonial card */}
            <div
              className="relative rounded-2xl p-6 mb-7 overflow-hidden"
              style={{
                background: "linear-gradient(135deg, #0f1f4b, #1a2f6e)",
                boxShadow: "0 16px 48px rgba(15,31,75,0.25)",
                opacity: animating ? 0 : 1,
                transform: animating ? "translateY(10px)" : "translateY(0)",
                transition: "opacity 0.3s ease, transform 0.3s ease",
              }}
            >
              {/* Quote icon — top right */}
              <div
                className="absolute top-0 right-0 w-12 h-12 flex items-center justify-center rounded-bl-2xl"
                style={{ background: "#42f551" }}
              >
                <svg viewBox="0 0 24 24" fill="white" className="w-5 h-5">
                  <path d="M6 17h3l2-4V7H5v6h3zm8 0h3l2-4V7h-6v6h3z" />
                </svg>
              </div>

              {/* Avatar + name */}
              <div className="flex items-center gap-3 mb-1">
                <img
                  src={profile}
                  alt={t.authorName}
                  className="w-12 h-12 rounded-full object-cover border-2 border-green-400"
                  style={{ boxShadow: "0 4px 12px rgba(234,88,12,0.3)" }}
                />
                <div>
                  <p
                    className="font-bold font-rajdhani uppercase text-white text-sm"
                  >
                   {t?.authorName || "Anonymous"}
                  </p>
                </div>
              </div>

              <StarRating rating={t.rating || 0} />

              <p
                className="text-blue-100/80 text-sm leading-relaxed"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                 "{t?.content || "No review available"}"
              </p>
            </div>

            {/* Dots */}
            <div className="flex items-center gap-2 mb-7">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => handleChange(i)}
                  className="transition-all duration-300 rounded-full"
                  style={{
                    width: i === active ? "28px" : "8px",
                    height: "8px",
                    background: i === active ? "#42f551" : "#cbd5e1",
                  }}
                />
              ))}
            </div>

            {/* CTA */}
            <button
             onClick={()=>{
              navigate("/user-blog");
              window.scrollTo({
                top: 0,
                behavior : "smooth"
              })
             }}
              className="group relative overflow-hidden flex items-center gap-3 bg-green-500 text-white font-bold uppercase text-sm px-7 py-4 rounded-xl w-fit transition-all duration-300 hover:shadow-xl hover:shadow-green-500/30"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: "0.1em" }}
            >
              <span className="relative z-10">Explore Our Blogs</span>
              <span className="relative z-10 w-7 h-7 bg-white/20 rounded-lg flex items-center justify-center transition-transform duration-300 group-hover:translate-x-1">
                →
              </span>
              <div
                className="absolute inset-0 bg-gray-900 transition-transform duration-500 origin-left scale-x-0 group-hover:scale-x-100"
                style={{ zIndex: 0 }}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}